// floorplanMerge.helpers.ts

import {
  GateMapping,
  PoiConnection,
  PoiGroup,
  PointOfInterest,
  Station,
  VehicleLayout,
} from '@thive/fddm-types';

import { Area } from '@/modules/floorplanDesign/types';

type MergeResult<TValue> = {
  value: TValue;
  removedElementIds: string[];
};

type SplitPoisByIncomingAreasResult = {
  keptPois: PointOfInterest[];
  removedPoiIds: Set<string>;
};

// Inlined to avoid circular imports between this helper and the mappers
// (the mapper module pulls in @modules/canvas, which transitively imports
// the floorplan design store).
const buildGateMappingId = (mapping: GateMapping): string =>
  mapping.sourcePoiIds.toString() + mapping.targetPoiIds.toString();

const getPoseIdsFromPois = (poiIds: string[]): string[] => poiIds.map((poiId) => `${poiId}-pose`);

export const collectAffectedStationAreaIds = (stations: Station[]): Set<string> =>
  stations.reduce((affectedAreaIds, station) => {
    station.areaIds.forEach((areaId) => {
      affectedAreaIds.add(areaId);
    });

    return affectedAreaIds;
  }, new Set<string>());

/** @description fills empty `station.areaIds` by matching `station.id` against `area.name` (id format: `${area.name}`). Required for manual areas */
export const linkStationsToAreasByName = (stations: Station[], areas: Area[]): Station[] => {
  if (areas.length === 0) return stations;

  return stations.map((station) => {
    if (station.areaIds.length > 0) return station;
    const matchingArea = areas.find((area) => station.id.startsWith(area.name));
    if (!matchingArea) return station;
    return { ...station, areaIds: [matchingArea.id] };
  });
};

export const mergeStations = (
  existingStations: Station[],
  incomingStations: Station[],
  areas: Area[] = [],
): MergeResult<Station[]> => {
  const linkedIncomingStations = linkStationsToAreasByName(incomingStations, areas);
  const affectedAreaIds = collectAffectedStationAreaIds(linkedIncomingStations);
  const removedElementIds: string[] = [];

  const stationsToKeep = existingStations.filter((station) => {
    const shouldRemove = station.areaIds.some((areaId) => affectedAreaIds.has(areaId));

    if (shouldRemove) {
      removedElementIds.push(station.id);
    }

    return !shouldRemove;
  });

  return {
    value:
      linkedIncomingStations.length === 0
        ? stationsToKeep
        : [...stationsToKeep, ...linkedIncomingStations],
    removedElementIds,
  };
};

export const mergePoiGroups = (
  existingGroups: PoiGroup[],
  incomingGroups: PoiGroup[],
): PoiGroup[] => {
  const incomingGroupsById = new Map<string, PoiGroup>();
  const existingGroupIds = new Set<string>();

  incomingGroups.forEach((group) => {
    incomingGroupsById.set(group.id, group);
  });

  const mergedGroups = existingGroups.map((group) => {
    existingGroupIds.add(group.id);

    return incomingGroupsById.get(group.id) ?? group;
  });

  incomingGroups.forEach((group) => {
    if (!existingGroupIds.has(group.id)) {
      mergedGroups.push(group);
    }
  });

  return mergedGroups;
};

export const collectIncomingPoiAreaIds = (incomingPois: PointOfInterest[]): Set<string> =>
  incomingPois.reduce((areaIds, poi) => {
    areaIds.add(poi.areaId);

    return areaIds;
  }, new Set<string>());

export const splitPoisByIncomingAreas = (
  existingPois: PointOfInterest[],
  incomingPois: PointOfInterest[],
): SplitPoisByIncomingAreasResult => {
  const incomingAreaIds = collectIncomingPoiAreaIds(incomingPois);
  const removedPoiIds = new Set<string>();

  const keptPois = existingPois.filter((poi) => {
    const shouldRemove = incomingAreaIds.has(poi.areaId);

    if (shouldRemove) {
      removedPoiIds.add(poi.id);
    }

    return !shouldRemove;
  });

  return {
    keptPois,
    removedPoiIds,
  };
};

export const mergePois = (
  keptPois: PointOfInterest[],
  incomingPois: PointOfInterest[],
): PointOfInterest[] => (incomingPois.length === 0 ? keptPois : [...keptPois, ...incomingPois]);

export const mergeGateMappings = (
  existingGateMappings: GateMapping[],
  incomingGateMappings: GateMapping[],
  removedPoiIds: Set<string>,
): MergeResult<GateMapping[]> => {
  const removedElementIds: string[] = [];

  const keptGateMappings = existingGateMappings.filter((gateMapping) => {
    const touchesRemovedPoi =
      gateMapping.sourcePoiIds.some((id) => removedPoiIds.has(id)) ||
      gateMapping.targetPoiIds.some((id) => removedPoiIds.has(id));

    if (touchesRemovedPoi) {
      removedElementIds.push(buildGateMappingId(gateMapping));
    }

    return !touchesRemovedPoi;
  });

  return {
    value:
      incomingGateMappings.length === 0
        ? keptGateMappings
        : [...keptGateMappings, ...incomingGateMappings],
    removedElementIds,
  };
};

export const mergePoiConnections = (
  existingConnections: PoiConnection[],
  incomingConnections: PoiConnection[],
  removedPoiIds: Set<string>,
): PoiConnection[] => {
  const keptConnections = existingConnections.filter(
    (connection) =>
      !removedPoiIds.has(connection.startPoiId) && !removedPoiIds.has(connection.endPoiId),
  );

  return incomingConnections.length === 0
    ? keptConnections
    : [...keptConnections, ...incomingConnections];
};

export const getRemovedPoiElementIds = (removedPoiIds: Set<string>): string[] => {
  if (removedPoiIds.size === 0) {
    return [];
  }

  const poiIds = [...removedPoiIds];
  const poseIds = getPoseIdsFromPois(poiIds);

  return [...poiIds, ...poseIds];
};

export const mergeVehicleLayout = (
  existingLayout: VehicleLayout,
  incomingLayout: VehicleLayout,
): MergeResult<VehicleLayout> => {
  const { keptPois, removedPoiIds } = splitPoisByIncomingAreas(
    existingLayout.pois,
    incomingLayout.pois,
  );

  const poiGroups = mergePoiGroups(existingLayout.poiGroups, incomingLayout.poiGroups);
  const pois = mergePois(keptPois, incomingLayout.pois);

  const gateMappingsResult = mergeGateMappings(
    existingLayout.gateMappings,
    incomingLayout.gateMappings,
    removedPoiIds,
  );

  const poiConnections = mergePoiConnections(
    existingLayout.poiConnections,
    incomingLayout.poiConnections,
    removedPoiIds,
  );

  return {
    value: {
      ...existingLayout,
      poiGroups,
      pois,
      gateMappings: gateMappingsResult.value,
      poiConnections,
    },
    removedElementIds: [
      ...gateMappingsResult.removedElementIds,
      ...getRemovedPoiElementIds(removedPoiIds),
    ],
  };
};

export const mergeVehicleLayouts = (
  existingLayouts: VehicleLayout[],
  incomingLayouts: VehicleLayout[],
): MergeResult<VehicleLayout[]> => {
  const removedElementIds: string[] = [];
  const incomingLayoutsByTypeId = new Map<string, VehicleLayout>();
  const existingTypeIds = new Set<string>();

  incomingLayouts.forEach((layout) => {
    incomingLayoutsByTypeId.set(layout.vehicleTypeId, layout);
  });

  const mergedLayouts = existingLayouts.map((existingLayout) => {
    existingTypeIds.add(existingLayout.vehicleTypeId);

    const incomingLayout = incomingLayoutsByTypeId.get(existingLayout.vehicleTypeId);

    if (!incomingLayout) {
      return existingLayout;
    }

    const mergedLayoutResult = mergeVehicleLayout(existingLayout, incomingLayout);

    removedElementIds.push(...mergedLayoutResult.removedElementIds);

    return mergedLayoutResult.value;
  });

  incomingLayouts.forEach((layout) => {
    if (!existingTypeIds.has(layout.vehicleTypeId)) {
      mergedLayouts.push(layout);
    }
  });

  return {
    value: mergedLayouts,
    removedElementIds,
  };
};
